import { visibleForRole } from '@/config/nav';
import type { Role } from '@/stores/session';
import { ADMIN_GUIDES } from './content/admin';
import { AI_GUIDES } from './content/ai';
import { ANALYTICS_GUIDES } from './content/analytics';
import { COMMAND_CENTER_GUIDES } from './content/commandCenter';
import { CRM_GUIDES } from './content/crm';
import { FINANCE_GUIDES } from './content/finance';
import { GUEST_GUIDES } from './content/guest';
import { HRM_GUIDES } from './content/hrm';
import { KB_GUIDES } from './content/kb';
import { LMS_GUIDES } from './content/lms';
import { MY_SPACE_GUIDES } from './content/mySpace';
import { PROJECT_GUIDES } from './content/projects';
import { WIKI_GUIDES } from './content/wiki';
import type { Guide, GuideGroup, GuideModule } from './types';

/** Display labels + ordering for the modules that have guides. */
const MODULE_LABELS: { module: GuideModule; label: string }[] = [
  { module: 'guest', label: 'Getting Started' },
  { module: 'my-space', label: 'My Space' },
  { module: 'crm', label: 'CRM' },
  { module: 'projects', label: 'Projects' },
  { module: 'wiki', label: 'Wiki' },
  { module: 'finance', label: 'Finance' },
  { module: 'hrm', label: 'People' },
  { module: 'lms', label: 'Learning (LMS)' },
  { module: 'command-center', label: 'Command Center' },
  { module: 'analytics', label: 'Insights' },
  { module: 'admin', label: 'Admin' },
  { module: 'kb', label: 'Knowledge Base' },
  { module: 'ai', label: 'AI & Integrations' },
];

/** Every guide in the app. New modules append their content here. */
export const GUIDES: Guide[] = [
  ...GUEST_GUIDES,
  ...MY_SPACE_GUIDES,
  ...CRM_GUIDES,
  ...PROJECT_GUIDES,
  ...WIKI_GUIDES,
  ...FINANCE_GUIDES,
  ...HRM_GUIDES,
  ...LMS_GUIDES,
  ...COMMAND_CENTER_GUIDES,
  ...ANALYTICS_GUIDES,
  ...ADMIN_GUIDES,
  ...KB_GUIDES,
  ...AI_GUIDES,
];

/**
 * Guides the role may see, optionally scoped to one module.
 *
 * Guests (clients) see ONLY the curated `guest` module; staff see every other
 * module but never the guest one — the two audiences get disjoint sets.
 */
export function visibleGuides(role: Role | null, module?: GuideModule): Guide[] {
  if (!role) return [];
  const inScope =
    role === 'guest'
      ? (g: Guide) => g.module === 'guest'
      : (g: Guide) => g.module !== 'guest' && visibleForRole(g.roles, role);
  return GUIDES.filter((g) => (!module || g.module === module) && inScope(g));
}

/** Visible guides grouped by module, in `MODULE_LABELS` order (empty groups dropped). */
export function visibleGuideGroups(role: Role | null, module?: GuideModule): GuideGroup[] {
  const guides = visibleGuides(role, module);
  return MODULE_LABELS.map(({ module: m, label }) => ({
    module: m,
    label,
    guides: guides.filter((g) => g.module === m),
  })).filter((group) => group.guides.length > 0);
}

export function guideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}
